import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Container from "components/__reusable/Container";
import PictureThumbnail from "components/__reusable/PictureThumbnail";
import Pagination from "components/__reusable/Pagination";
import LoadingDialog from "components/__reusable/LoadingDialog";
import PictureDialog from "./PictureDialog";
import { getPublishedPictures } from "services/pictureServices";
import type { Picture } from "types/Pictures";
import { PICTURE_PAGE_SIZE, PictureStudentOption } from "config/constants";
import PictureSearchInput from "components/__reusable/PictureSearchInput";

type Props = {
  startDate?: string;
  endDate?: string;
  classId?: number;
};

const Pictures: React.FC<Props> = (props: Props) => {
  const {
    startDate: propStartDate,
    endDate: propEndDate,
    classId: propClassId,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Local state management
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [searchStartDate, setSearchStartDate] = useState<string>(
    propStartDate || ""
  );
  const [searchEndDate, setSearchEndDate] = useState<string>(propEndDate || "");
  const [searchClassId, setSearchClassId] = useState<number | undefined>(
    propClassId
  );
  const [studentId, setStudentId] = useState<
    number | PictureStudentOption | null | undefined
  >();
  const [hashtagId, setHashtagId] = useState<number | undefined>();
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewIndex, setViewIndex] = useState(-1);

  // Track if component is mounted to prevent initial duplicate calls
  const isMounted = useRef(false);

  // Memoize initial values to prevent unnecessary re-renders
  const initialSearchValues = useRef({
    startDate: propStartDate || "",
    endDate: propEndDate || "",
    classId: propClassId,
    studentId: undefined,
    hashtagId: undefined,
    isFavorite: false,
  });

  useEffect(() => {
    fetchPictures(0);
  }, []);

  // Initialize component with props
  useEffect(() => {
    if (propStartDate && propEndDate) {
      setSearchStartDate(propStartDate);
      setSearchEndDate(propEndDate);
    }
    if (typeof propClassId !== "undefined") {
      setSearchClassId(propClassId);
    }
    // Mark component as mounted after initial setup
    isMounted.current = true;
  }, [propStartDate, propEndDate, propClassId]);

  // Fetch pictures function
  const fetchPictures = async (pageNum: number) => {
    setLoading(true);
    try {
      if (!searchStartDate || !searchEndDate) {
        setPictures([]);
        setTotal(0);
        return;
      }
      const startDate = new Date(searchStartDate);
      const endDate = new Date(searchEndDate);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        setPictures([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(23, 59, 59, 999);
      const result = await getPublishedPictures(
        pageNum + 1,
        startDate.toISOString(),
        endDate.toISOString(),
        searchClassId,
        studentId,
        hashtagId,
        isFavorite
      );
      setPictures(result.results);
      setTotal(result.count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const refetchPictures = async () => {
    setPage(0);
    fetchPictures(0);
  };

  const renderThumbnail = (picture: Picture) => (
    <button
      key={picture.id}
      className="w-16 h-16 me-3 mb-3 cursor-pointer"
      onClick={() =>
        picture.thumbnail_picture ? viewPicture(picture.id) : null
      }
    >
      <PictureThumbnail imageSrc={picture.thumbnail_picture} name="picture" />
    </button>
  );

  const viewPicture = (pictureId: number) => {
    if (!pictures) return;
    const index = pictures.findIndex((picture) => picture.id === pictureId);
    setViewIndex(index);
  };

  const viewPreviousPicture = useCallback(() => {
    if (!pictures) return;
    let index = viewIndex;
    if (index === 0) return;
    index--;
    setViewIndex(index);
  }, [pictures, viewIndex]);

  const viewNextPicture = useCallback(() => {
    if (!pictures) return;
    let index = viewIndex;
    if (index === pictures.length - 1) return;
    index++;
    setViewIndex(index);
  }, [pictures, viewIndex]);

  const handleKeyboardEvent = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        viewNextPicture();
        return;
      }
      if (e.key === "ArrowLeft") {
        viewPreviousPicture();
        return;
      }
    },
    [viewNextPicture, viewPreviousPicture]
  );

  useEffect(() => {
    window.removeEventListener("keyup", handleKeyboardEvent);
    window.addEventListener("keyup", handleKeyboardEvent);
    return () => {
      window.removeEventListener("keyup", handleKeyboardEvent);
    };
  }, [handleKeyboardEvent]);

  // Handle back button - go to previous URL
  const handleBackButton = () => {
    navigate(-1);
  };

  // Search handlers for PictureSearchInput - memoized to prevent unnecessary re-renders
  const handleSearchChange = useCallback(
    (searchParams: {
      startDate?: string;
      endDate?: string;
      classId?: number;
      studentId?: number | PictureStudentOption | null;
      hashtagId?: number;
      isFavorite?: boolean;
    }) => {
      // Skip the first call during component initialization
      if (!isMounted.current) {
        return;
      }
      setSearchStartDate(searchParams.startDate || "");
      setSearchEndDate(searchParams.endDate || "");
      setSearchClassId(searchParams.classId);
      setStudentId(searchParams.studentId);
      setHashtagId(searchParams.hashtagId);
      setIsFavorite(searchParams.isFavorite || false);
    },
    []
  );

  return (
    <div className="h-full w-full flex flex-col space-y-1 lg:space-y-3">
      <div>
        <PictureSearchInput
          onSearch={refetchPictures}
          onSearchChange={handleSearchChange}
          initialValues={initialSearchValues.current}
        />
      </div>
      <div className="flex-1 flex flex-col space-y-1 lg:space-y-2">
        {pictures && (
          <div className="lg:flex lg:justify-between lg:items-center">
            {total >= 1000 && (
              <p className="text-[#ff0000]">
                {t("_published_picture._large_data_warning")}
              </p>
            )}
            <p>
              {t("_published_picture._total_result")}
              {total || 0}
            </p>
          </div>
        )}
        <div className="flex-1">
          <Container>
            <div className="h-full overflow-auto relative">
              <div className="w-full h-full absolute">
                {loading && <LoadingDialog />}
                {pictures && (
                  <div className="flex flex-wrap">
                    {pictures.length === 0 && <p>{t("_generic._no_data")}</p>}
                    {pictures.map((picture) => renderThumbnail(picture))}
                  </div>
                )}
              </div>
            </div>
          </Container>
        </div>
        {pictures && total > PICTURE_PAGE_SIZE && (
          <Pagination
            currentPage={page}
            pageSize={PICTURE_PAGE_SIZE}
            totalItems={total}
            onChangePage={(page) => setPage(page)}
          />
        )}
      </div>
      {viewIndex >= 0 && pictures[viewIndex] && (
        <PictureDialog
          picture={pictures[viewIndex]}
          onPrevious={viewIndex > 0 ? () => viewPreviousPicture() : null}
          onNext={
            viewIndex < pictures.length - 1 ? () => viewNextPicture() : null
          }
          onClose={() => setViewIndex(-1)}
        />
      )}
    </div>
  );
};

export default Pictures;
